import * as monaco from "monaco-editor";
import { PlainTextOperation as TextOperation, } from "@operational-transformation/plaintext";
import { Cursor, EditorAdapterEvent, } from "@operational-transformation/plaintext-editor";
import mitt from "mitt";
import { CursorWidgetController, } from "./cursor-widget-controller";
import * as Utils from "./utils";
export class MonacoAdapter {
    constructor(monacoInstance, avoidListeners = true) {
        var _a;
        this._classNames = [];
        this._disposables = [];
        this._monaco = monacoInstance;
        this._lastDocLines = ((_a = this._monaco.getModel()) === null || _a === void 0 ? void 0 : _a.getLinesContent()) || [""];
        this._lastCursorRange = this._monaco.getSelection();
        this._remoteCursors = new Map();
        this._cursorWidgetController = new CursorWidgetController(this._monaco);
        this._redoCallback = null;
        this._undoCallback = null;
        this._originalRedo = null;
        this._originalUndo = null;
        this._ignoreChanges = false;
        if (!avoidListeners) {
            this._init();
        }
    }
    deregisterUndo(callback) {
        const model = this._getModel();
        if (!model) {
            return;
        }
        if (model.undo !== this._originalUndo) {
            model.undo = this._originalUndo;
        }
        this._originalUndo = null;
    }
    deregisterRedo(callback) {
        const model = this._getModel();
        if (!model) {
            return;
        }
        if (model.redo !== this._originalRedo) {
            model.redo = this._originalRedo;
        }
        this._originalRedo = null;
    }
    _init() {
        this._emitter = mitt();
        this._disposables.push(this._cursorWidgetController, this._monaco.onDidBlurEditorWidget(() => {
            this._onBlur();
        }), this._monaco.onDidFocusEditorWidget(() => {
            this._onFocus();
        }), this._monaco.onDidChangeModel((ev) => {
            this._onModelChange(ev);
        }), this._monaco.onDidChangeModelContent((ev) => {
            this._onChange(ev);
        }), this._monaco.onDidChangeCursorPosition((ev) => {
            this._onCursorActivity(ev);
        }));
    }
    dispose() {
        this._remoteCursors.clear();
        this._disposables.forEach((disposable) => disposable.dispose());
        this._disposables.splice(0, this._disposables.length);
        if (this._emitter) {
            this._emitter.all.clear();
            this._emitter = null;
        }
        const model = this._getModel();
        if (!model) {
            return;
        }
        if (model.undo !== this._originalUndo) {
            model.undo = this._originalUndo;
        }
        if (model.redo !== this._originalRedo) {
            model.redo = this._originalRedo;
        }
        this._originalUndo = null;
        this._originalRedo = null;
    }
    _getModel() {
        return this._monaco.getModel();
    }
    registerUndo(callback) {
        const model = this._getModel();
        if (!model) {
            return;
        }
        this._originalUndo = model.undo;
        model.undo = this._undoCallback = callback;
    }
    registerRedo(callback) {
        const model = this._getModel();
        if (!model) {
            return;
        }
        this._originalRedo = model.redo;
        model.redo = this._redoCallback = callback;
    }
    on(event, listener) {
        var _a;
        return (_a = this._emitter) === null || _a === void 0 ? void 0 : _a.on(event, listener);
    }
    off(event, listener) {
        var _a;
        return (_a = this._emitter) === null || _a === void 0 ? void 0 : _a.off(event, listener);
    }
    _trigger(event, payload) {
        return this._emitter.emit(event, payload);
    }
    getCursor() {
        const model = this._getModel();
        if (!model) {
            return null;
        }
        let selection = this._monaco.getSelection();
        if (selection == null) {
            selection = this._lastCursorRange;
        }
        const startPos = selection.getStartPosition();
        const endPos = selection.getEndPosition();
        let start = model.getOffsetAt(startPos);
        let end = model.getOffsetAt(endPos);
        if (start > end) {
            [start, end] = [end, start];
        }
        return new Cursor(start, end);
    }
    setCursor(cursor) {
        const { position, selectionEnd } = cursor.toJSON();
        const model = this._getModel();
        if (!model) {
            return;
        }
        let start = model.getPositionAt(position);
        let end = model.getPositionAt(selectionEnd);
        if (position > selectionEnd) {
            [start, end] = [end, start];
        }
        this._monaco.setSelection(new monaco.Range(start.lineNumber, start.column, end.lineNumber, end.column));
    }
    setOtherCursor({ clientId, cursor, userColor, userName, }) {
        Utils.validateTruth(typeof cursor === "object" &&
            typeof cursor.toJSON === "function" &&
            typeof userColor === "string" &&
            (typeof clientId === "string" || typeof clientId === "number") &&
            !!userColor.match(/^#[a-fA-F0-9]{3,6}$/));
        const { position, selectionEnd } = cursor.toJSON();
        Utils.validateFalse(position < 0 || selectionEnd < 0);
        let remoteCursor = this._remoteCursors.get(clientId);
        if (!remoteCursor) {
            remoteCursor = {
                clientId,
                decoration: [],
            };
            this._remoteCursors.set(clientId, remoteCursor);
        }
        else {
            remoteCursor.decoration = this._monaco.deltaDecorations(remoteCursor.decoration, []);
        }
        let selectionColor = userColor;
        let className = `remote-client-selection-${userColor.replace("#", "")}`;
        if (position === selectionEnd) {
            selectionColor = "transparent";
            className = className.replace("selection", "cursor");
        }
        this._addStyleRule(className, selectionColor, userColor);
        const model = this._getModel();
        if (!model) {
            return {
                dispose: Utils.noop,
            };
        }
        let start = model.getPositionAt(position);
        let end = model.getPositionAt(selectionEnd);
        if (start > end) {
            [start, end] = [end, start];
        }
        const range = new monaco.Range(start.lineNumber, start.column, end.lineNumber, end.column);
        remoteCursor.decoration = this._monaco.deltaDecorations(remoteCursor.decoration, [
            {
                range,
                options: {
                    className,
                    isWholeLine: false,
                    stickiness: monaco.editor.TrackedRangeStickiness.NeverGrowsWhenTypingAtEdges,
                },
            },
        ]);
        this._cursorWidgetController.updateCursor(clientId, range, userColor, userName);
        return {
            dispose: () => {
                const cursor = this._remoteCursors.get(clientId);
                if (!cursor) {
                    return;
                }
                cursor.decoration = this._monaco.deltaDecorations(cursor.decoration, []);
            },
        };
    }
    getText() {
        const model = this._getModel();
        if (model) {
            return model.getValue();
        }
        return "";
    }
    setText(text) {
        const model = this._getModel();
        if (!model) {
            return;
        }
        model.applyEdits([
            {
                range: model.getFullModelRange(),
                text,
            },
        ]);
    }
    setInitiated(init) {
        this.setText("");
        this._initiated = init;
    }
    _getPreviousContentInRange(range) {
        const model = this._getModel();
        const eol = model ? model.getEOL() : Utils.EndOfLineSequence.LF;
        if (!range) {
            return this._lastDocLines.join(eol);
        }
        if (range.isEmpty()) {
            return "";
        }
        let val = "";
        const { startLineNumber, startColumn, endLineNumber, endColumn } = range;
        for (let i = startLineNumber; i <= endLineNumber; i++) {
            const line = this._lastDocLines[i - 1];
            if (i === startLineNumber) {
                if (i === endLineNumber) {
                    return line.slice(startColumn - 1, endColumn - 1);
                }
                val += line.slice(startColumn - 1) + eol;
            }
            else if (i === endLineNumber) {
                val += line.slice(0, endColumn - 1);
            }
            else {
                val += line + eol;
            }
        }
        return val;
    }
    _transformOpsIntoMonacoChanges(ops, model) {
        let index = 0;
        const changes = [];
        let opValue;
        while (!(opValue = ops.next()).done) {
            const op = opValue.value[1];
            if (op.isRetain()) {
                index += op.characterCount();
                continue;
            }
            if (op.isInsert()) {
                const pos = model.getPositionAt(index);
                changes.push({
                    range: new monaco.Range(pos.lineNumber, pos.column, pos.lineNumber, pos.column),
                    text: op.textContent(),
                    forceMoveMarkers: true,
                });
                continue;
            }
            if (op.isDelete()) {
                const from = model.getPositionAt(index);
                const to = model.getPositionAt(index + op.characterCount());
                changes.push({
                    range: new monaco.Range(from.lineNumber, from.column, to.lineNumber, to.column),
                    text: "",
                    forceMoveMarkers: true,
                });
                index += op.characterCount();
            }
        }
        return changes;
    }
    _applyChangesToMonaco(changes) {
        let readOnly;
        readOnly = this._monaco.getOption(monaco.editor.EditorOption.readOnly);
        if (readOnly) {
            this._monaco.updateOptions({ readOnly: false });
        }
        this._monaco.executeEdits("firepad", changes);
        if (readOnly) {
            this._monaco.updateOptions({ readOnly });
        }
    }
    applyOperation(operation) {
        if (!operation.isNoop()) {
            this._ignoreChanges = true;
        }
        const model = this._getModel();
        if (!model) {
            return;
        }
        const changes = this._transformOpsIntoMonacoChanges(operation.entries(), model);
        if (changes.length) {
            this._applyChangesToMonaco(changes);
        }
        if (model) {
            this._lastDocLines = model.getLinesContent();
        }
        this._ignoreChanges = false;
    }
    invertOperation(operation) {
        return operation.invert(this.getText());
    }
    _onBlur() {
        const currentSelecton = this._monaco.getSelection();
        if (!currentSelecton || currentSelecton.isEmpty()) {
            this._trigger(EditorAdapterEvent.Blur, undefined);
        }
    }
    _onFocus() {
        this._trigger(EditorAdapterEvent.Focus, undefined);
    }
    _onCursorActivity(ev) {
        if (ev.reason === monaco.editor.CursorChangeReason.RecoverFromMarkers) {
            return;
        }
        this._trigger(EditorAdapterEvent.Cursor, undefined);
    }
    _onChange(ev) {
        if (this._ignoreChanges || !this._initiated) {
            return;
        }
        const model = this._getModel();
        const content = this._getPreviousContentInRange();
        const contentLength = content.length;
        if (!ev.changes || ev.changes.length === 0) {
            const op = new TextOperation().retain(contentLength);
            this._trigger(EditorAdapterEvent.Change, {
                operation: op,
                inverse: op,
            });
            return;
        }
        const [mainOp, reverseOp] = this._operationFromMonacoChange(ev.changes, contentLength);
        this._lastDocLines = model.getLinesContent();
        this._trigger(EditorAdapterEvent.Change, {
            operation: mainOp,
            inverse: reverseOp,
        });
    }
    _onModelChange(_ev) {
        const newModel = this._getModel();
        if (!newModel) {
            return;
        }
        if (this._undoCallback) {
            this._originalUndo = newModel.undo;
            newModel.undo = this._undoCallback;
        }
        if (this._redoCallback) {
            this._originalRedo = newModel.redo;
            newModel.redo = this._redoCallback;
        }
        const oldLinesCount = this._lastDocLines.length;
        const oldLastColumLength = this._lastDocLines[oldLinesCount - 1].length;
        const oldRange = new monaco.Range(1, 1, oldLinesCount, oldLastColumLength + 1);
        const oldValue = this._getPreviousContentInRange();
        this._onChange({
            changes: [
                {
                    range: oldRange,
                    rangeOffset: 0,
                    rangeLength: oldValue.length,
                    text: newModel.getValue(),
                },
            ],
        });
    }
    _operationFromMonacoChange(changes, contentLength) {
        let mainOp = new TextOperation();
        let reverseOp = new TextOperation();
        if (changes.length > 1) {
            const first = changes[0];
            const last = changes[changes.length - 1];
            if (first.rangeOffset > last.rangeOffset) {
                changes = changes.reverse();
            }
        }
        let skippedChars = 0;
        for (const change of changes) {
            const { range, text, rangeOffset, rangeLength } = change;
            const retain = rangeOffset - skippedChars;
            try {
                mainOp = mainOp.retain(retain);
                reverseOp = reverseOp.retain(retain);
            }
            catch (err) {
                this._trigger(EditorAdapterEvent.Error, {
                    err,
                    operation: mainOp.toString(),
                    retain,
                });
                throw err;
            }
            if (!text && !range.isEmpty()) {
                mainOp = mainOp.delete(rangeLength);
                reverseOp = reverseOp.insert(this._getPreviousContentInRange(range));
            }
            else if (text && !range.isEmpty()) {
                mainOp = mainOp.delete(rangeLength).insert(text);
                reverseOp = reverseOp
                    .insert(this._getPreviousContentInRange(range))
                    .delete(text);
            }
            else {
                mainOp = mainOp.insert(text);
                reverseOp = reverseOp.delete(text);
            }
            skippedChars = skippedChars + retain + rangeLength;
        }
        try {
            mainOp = mainOp.retain(contentLength - skippedChars);
            reverseOp = reverseOp.retain(contentLength - skippedChars);
        }
        catch (err) {
            this._trigger(EditorAdapterEvent.Error, {
                err,
                operation: mainOp.toString(),
                contentLength,
                skippedChars,
            });
            throw err;
        }
        return [mainOp, reverseOp];
    }
    _getStyles(className, backgroundColor, fontColor) {
        return `
      .${className} {
        position: relative;
        background-color: ${backgroundColor};
        border-left: 2px solid ${fontColor};
      }
    `;
    }
    _addStyleRule(className, backgroundColor, fontColor) {
        Utils.validateTruth(document != null, "This package must run on browser!");
        if (this._classNames.includes(className)) {
            return;
        }
        const style = this._getStyles(className, backgroundColor, fontColor);
        const styleTextNode = document.createTextNode(style);
        const styleElement = document.createElement("style");
        styleElement.appendChild(styleTextNode);
        document.head.appendChild(styleElement);
        this._classNames.push(className);
    }
}
